import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

// Defined outside component so React doesn't remount on every render
function ActionButtons({ booking, onComplete, onReschedule, onCancel, onNoShow }) {
  return (
    <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
      <button className="btn btn-sm btn-primary" onClick={(e) => { e.stopPropagation(); onComplete(booking.id); }}>
        Arrived
      </button>
      <button className="btn btn-sm btn-secondary" onClick={(e) => { e.stopPropagation(); onReschedule(booking); }}>
        Reschedule
      </button>
      <button className="btn btn-sm btn-secondary" onClick={(e) => { e.stopPropagation(); onCancel(booking.id, booking.customer_name); }}>
        Cancel
      </button>
      <button className="btn btn-sm btn-danger" onClick={(e) => { e.stopPropagation(); onNoShow(booking.id, booking.customer_name); }}>
        No Show
      </button>
    </div>
  );
}

function BookingModal({ children, onClose, isMobile }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200,
      display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
      padding: isMobile ? 0 : '2rem'
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: 'white', borderRadius: isMobile ? '16px 16px 0 0' : '16px',
        padding: isMobile ? '1.5rem' : '2rem', width: '100%',
        maxWidth: isMobile ? '100%' : '480px', maxHeight: isMobile ? '90vh' : '80vh',
        overflowY: 'auto', WebkitOverflowScrolling: 'touch'
      }}>
        {children}
      </div>
    </div>
  );
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

export default function BookingCalendar() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [studios, setStudios] = useState([]);
  const [selectedStudio, setSelectedStudio] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedBooking, setExpandedBooking] = useState(null);

  // Create booking modal
  const [showCreate, setShowCreate] = useState(false);
  const [createDate, setCreateDate] = useState('');
  const [createTime, setCreateTime] = useState('');
  const [createFirstName, setCreateFirstName] = useState('');
  const [createLastName, setCreateLastName] = useState('');
  const [createPhone, setCreatePhone] = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [createNotes, setCreateNotes] = useState('');
  const [createSource, setCreateSource] = useState('manual');
  const [createSubmitting, setCreateSubmitting] = useState(false);

  // Reschedule modal
  const [showReschedule, setShowReschedule] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleSubmitting, setRescheduleSubmitting] = useState(false);

  const formatDate = (date) => {
    const d = new Date(date);
    return d.getFullYear() + '-' +
      String(d.getMonth() + 1).padStart(2, '0') + '-' +
      String(d.getDate()).padStart(2, '0');
  };

  const getMonday = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };

  const fetchStudios = useCallback(async () => {
    try {
      const res = await api.get('/booking/studios');
      setStudios(res.data.studios);
      if (res.data.studios.length > 0 && !selectedStudio) {
        setSelectedStudio(res.data.studios[0].id);
      }
    } catch (err) {
      setError('Failed to load studios');
    }
  }, [selectedStudio]);

  const fetchBookings = useCallback(async () => {
    if (!selectedStudio) return;
    setLoading(true);
    try {
      if (viewMode === 'day') {
        const res = await api.get('/booking/upcoming', {
          params: { studio_id: selectedStudio, date: formatDate(currentDate) }
        });
        setBookings(res.data.bookings);
      } else {
        const monday = getMonday(currentDate);
        const res = await api.get('/booking/week', {
          params: { studio_id: selectedStudio, start_date: formatDate(monday) }
        });
        setBookings(res.data.bookings);
      }
    } catch (err) {
      setError('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [selectedStudio, currentDate, viewMode]);

  useEffect(() => { fetchStudios(); }, [fetchStudios]);
  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(t);
    }
  }, [success]);

  useEffect(() => {
    setExpandedBooking(null);
  }, [viewMode, currentDate]);

  const handleNoShow = async (bookingId, name) => {
    if (!window.confirm(`Mark ${name} as no-show?`)) return;
    setError('');
    try {
      await api.post(`/booking/${bookingId}/no-show`);
      setSuccess(`${name} marked as no-show`);
      setExpandedBooking(null);
      fetchBookings();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to mark no-show');
    }
  };

  const handleComplete = async (bookingId) => {
    setError('');
    try {
      await api.post(`/booking/${bookingId}/complete`);
      setSuccess('Marked as arrived');
      setExpandedBooking(null);
      fetchBookings();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update booking');
    }
  };

  const handleCancel = async (bookingId, name) => {
    if (!window.confirm(`Cancel booking for ${name}?`)) return;
    setError('');
    try {
      await api.post(`/booking/${bookingId}/staff-cancel`);
      setSuccess(`Booking for ${name} cancelled`);
      setExpandedBooking(null);
      fetchBookings();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel');
    }
  };

  const selectedStudioData = studios.find(s => s.id === selectedStudio);
  const generateTimeSlots = useCallback(() => {
    if (!selectedStudioData) return [];
    const slots = [];
    const [openH, openM] = selectedStudioData.opening_time.split(':').map(Number);
    const [closeH, closeM] = selectedStudioData.closing_time.split(':').map(Number);
    const duration = selectedStudioData.slot_duration_minutes;
    let current = openH * 60 + openM;
    const end = closeH * 60 + closeM;
    while (current + duration <= end) {
      const h = Math.floor(current / 60);
      const m = current % 60;
      slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
      current += duration;
    }
    return slots;
  }, [selectedStudioData]);

  const handleCreate = async () => {
    const createName = `${createFirstName.trim()} ${createLastName.trim()}`.trim();
    if (!createFirstName.trim() || !createLastName.trim() || !createTime || !createDate || !createEmail.trim()) {
      setError('First name, last name, email, date, and time are required');
      return;
    }
    setCreateSubmitting(true);
    setError('');
    try {
      await api.post('/booking/staff-create', {
        studio_id: selectedStudio,
        customer_name: createName,
        customer_phone: createPhone || null,
        customer_email: createEmail || null,
        date: createDate,
        time: createTime,
        notes: createNotes || null,
        source: createSource
      });
      setSuccess('Booking created');
      setShowCreate(false);
      setCreateFirstName(''); setCreateLastName(''); setCreatePhone(''); setCreateEmail('');
      setCreateNotes(''); setCreateTime(''); setCreateDate('');
      setCreateSource('manual');
      fetchBookings();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create booking');
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handleToggleInfluencer = async (bookingId, currentVal) => {
    try {
      await api.post(`/booking/${bookingId}/influencer`);
      setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, is_influencer: !currentVal } : b));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update');
    }
  };

  const openReschedule = (booking) => {
    setShowReschedule(booking);
    setRescheduleDate(booking.date);
    setRescheduleTime(booking.time);
  };

  const handleReschedule = async () => {
    if (!rescheduleDate || !rescheduleTime) {
      setError('Date and time are required');
      return;
    }
    setRescheduleSubmitting(true);
    setError('');
    try {
      await api.post(`/booking/${showReschedule.id}/reschedule`, {
        date: rescheduleDate,
        time: rescheduleTime
      });
      setSuccess(`Booking rescheduled to ${rescheduleDate} at ${rescheduleTime}`);
      setShowReschedule(null);
      setExpandedBooking(null);
      fetchBookings();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reschedule');
    } finally {
      setRescheduleSubmitting(false);
    }
  };

  const openCreateModal = (prefilledDate, prefilledTime) => {
    setCreateDate(prefilledDate || formatDate(new Date()));
    setCreateTime(prefilledTime || '');
    setShowCreate(true);
  };

  const toggleExpand = (bookingId) => {
    setExpandedBooking(expandedBooking === bookingId ? null : bookingId);
  };

  const navigateDate = (direction) => {
    const d = new Date(currentDate);
    if (viewMode === 'day') {
      d.setDate(d.getDate() + direction);
    } else {
      d.setDate(d.getDate() + (direction * 7));
    }
    setCurrentDate(d);
  };

  const goToToday = () => setCurrentDate(new Date());

  const formatDisplayDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      weekday: isMobile ? 'short' : 'long', day: 'numeric', month: isMobile ? 'short' : 'long', year: 'numeric'
    });
  };

  const formatWeekRange = (date) => {
    const monday = getMonday(date);
    const sunday = new Date(monday);
    sunday.setDate(sunday.getDate() + 6);
    const opts = { day: 'numeric', month: 'short' };
    return `${monday.toLocaleDateString('en-GB', opts)} — ${sunday.toLocaleDateString('en-GB', opts)}`;
  };

  const statusBadge = (status) => {
    const classes = { confirmed: 'badge-active', completed: 'badge-owner', no_show: 'badge-inactive', cancelled: 'badge-inactive' };
    return classes[status] || 'badge-inactive';
  };

  const statusLabel = (status) => {
    const labels = { confirmed: 'Confirmed', completed: 'Completed', no_show: 'No Show', cancelled: 'Cancelled' };
    return labels[status] || status;
  };

  // Group bookings by date for week view
  const bookingsByDate = {};
  if (viewMode === 'week') {
    const monday = getMonday(currentDate);
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(d.getDate() + i);
      const key = formatDate(d);
      bookingsByDate[key] = {
        label: d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }),
        bookings: []
      };
    }
    bookings.forEach(b => {
      if (bookingsByDate[b.date]) {
        bookingsByDate[b.date].bookings.push(b);
      }
    });
  }

  const timeSlots = viewMode === 'day' ? generateTimeSlots() : [];
  const bookingsByTime = {};
  if (viewMode === 'day') {
    bookings.forEach(b => {
      bookingsByTime[b.time] = b;
    });
  }

  return (
    <div>
      <div className="page-header">
        <h1>Bookings</h1>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button className="btn btn-sm btn-primary" onClick={() => openCreateModal()}>
            + New
          </button>
          <button
            className={`btn btn-sm ${viewMode === 'day' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('day')}
          >Day</button>
          <button
            className={`btn btn-sm ${viewMode === 'week' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('week')}
          >Week</button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Navigation controls */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <button className="btn btn-sm btn-secondary" onClick={() => navigateDate(-1)}>←</button>
              <button className="btn btn-sm btn-secondary" onClick={goToToday}>Today</button>
              <button className="btn btn-sm btn-secondary" onClick={() => navigateDate(1)}>→</button>
            </div>
            <strong style={{ fontSize: isMobile ? '0.85rem' : '1.05rem' }}>
              {viewMode === 'day' ? formatDisplayDate(currentDate) : formatWeekRange(currentDate)}
            </strong>
          </div>
          <div className="form-group" style={{ marginBottom: 0, minWidth: isMobile ? '100%' : '200px' }}>
            <select value={selectedStudio} onChange={e => setSelectedStudio(e.target.value)} style={{ padding: '0.4rem 0.6rem' }}>
              {studios.map(s => (
                <option key={s.id} value={s.id}>{s.name} ({s.country})</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading bookings...</div>
      ) : viewMode === 'day' ? (
        /* ======================== DAY VIEW ======================== */
        <div className="card">
          <h2 style={{ marginTop: 0, marginBottom: '1rem', fontSize: isMobile ? '1rem' : '1.1rem' }}>
            {bookings.filter(b => b.status === 'confirmed').length} confirmed
          </h2>
          <div className="booking-day-grid">
            {timeSlots.map(time => {
              const booking = bookingsByTime[time];
              const isExpanded = booking && expandedBooking === booking.id;
              return (
                <div key={time} className={`booking-slot ${booking ? 'booking-slot-filled' : ''}`}>
                  <div className="booking-slot-time">{time}</div>
                  {booking ? (
                    <div
                      className="booking-slot-content"
                      onClick={() => toggleExpand(booking.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="booking-slot-name">
                        {booking.customer_id ? (
                          <strong
                            style={{ cursor: 'pointer', textDecoration: 'underline', color: '#4f46e5' }}
                            onClick={e => { e.stopPropagation(); navigate(`/customers/${booking.customer_id}`); }}
                          >{booking.customer_name}</strong>
                        ) : (
                          <strong>{booking.customer_name}</strong>
                        )}
                        <span className={`badge ${statusBadge(booking.status)}`} style={{ marginLeft: '0.5rem' }}>
                          {statusLabel(booking.status)}
                        </span>
                      </div>

                      {isExpanded && (
                        <div style={{ marginTop: '0.4rem' }}>
                          <div className="booking-slot-contact">
                            {booking.customer_phone || booking.customer_email || '—'}
                          </div>
                          {booking.notes && (
                            <div className="booking-slot-notes">{booking.notes}</div>
                          )}
                          <div style={{ marginTop: '0.4rem' }}>
                            <button
                              className={`btn btn-sm ${booking.is_influencer ? 'btn-primary' : 'btn-secondary'}`}
                              onClick={e => { e.stopPropagation(); handleToggleInfluencer(booking.id, booking.is_influencer); }}
                            >
                              {booking.is_influencer ? '⭐ Influencer' : '☆ Mark as Influencer'}
                            </button>
                          </div>
                          {booking.status === 'confirmed' && (
                            <ActionButtons booking={booking} onComplete={handleComplete} onReschedule={openReschedule} onCancel={handleCancel} onNoShow={handleNoShow} />
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      className="booking-slot-empty"
                      onClick={() => openCreateModal(formatDate(currentDate), time)}
                      style={{ cursor: 'pointer' }}
                    >
                      + Available
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* ======================== WEEK VIEW ======================== */
        <div className="card" style={{ overflowX: 'auto' }}>
          <div className="booking-week-grid">
            {Object.entries(bookingsByDate).map(([date, data]) => (
              <div key={date} className="booking-week-day" style={date === formatDate(new Date()) ? { border: '2px solid #4ecca3', background: '#f0fdf7' } : {}}>
                <div className="booking-week-day-header">
                  <strong style={{ fontSize: isMobile ? '0.8rem' : '0.85rem' }}>{data.label}</strong>
                  <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                    {data.bookings.filter(b => b.status === 'confirmed').length}
                  </span>
                </div>
                {data.bookings.length === 0 ? (
                  <div className="booking-week-empty">—</div>
                ) : (
                  data.bookings.map(b => {
                    const isExpanded = expandedBooking === b.id;
                    return (
                      <div
                        key={b.id}
                        className="booking-week-item"
                        onClick={() => toggleExpand(b.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <strong>{b.time}{b.is_influencer ? ' ⭐' : ''}</strong>
                          <span className={`badge ${statusBadge(b.status)}`}>{statusLabel(b.status)}</span>
                        </div>
                        {b.customer_id ? (
                          <div
                            style={{ fontSize: isMobile ? '0.8rem' : '0.85rem', cursor: 'pointer', textDecoration: 'underline', color: '#4f46e5' }}
                            onClick={e => { e.stopPropagation(); navigate(`/customers/${b.customer_id}`); }}
                          >{b.customer_name}</div>
                        ) : (
                          <div style={{ fontSize: isMobile ? '0.8rem' : '0.85rem' }}>{b.customer_name}</div>
                        )}

                        {isExpanded ? (
                          <div style={{ marginTop: '0.3rem' }}>
                            <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                              {b.customer_phone || b.customer_email || '—'}
                            </div>
                            {b.notes && (
                              <div style={{ fontSize: '0.8rem', color: '#6b7280', fontStyle: 'italic', marginTop: '0.2rem' }}>
                                {b.notes}
                              </div>
                            )}
                            <div style={{ marginTop: '0.3rem' }}>
                              <button
                                className={`btn btn-sm ${b.is_influencer ? 'btn-primary' : 'btn-secondary'}`}
                                onClick={e => { e.stopPropagation(); handleToggleInfluencer(b.id, b.is_influencer); }}
                              >
                                {b.is_influencer ? '⭐ Influencer' : '☆ Mark as Influencer'}
                              </button>
                            </div>
                            {b.status === 'confirmed' && (
                              <ActionButtons booking={b} onComplete={handleComplete} onReschedule={openReschedule} onCancel={handleCancel} onNoShow={handleNoShow} />
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                  })
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ======================== CREATE BOOKING MODAL ======================== */}
      {showCreate && (
        <BookingModal isMobile={isMobile} onClose={() => setShowCreate(false)}>
          <h2 style={{ marginTop: 0, marginBottom: '0.25rem' }}>New Booking</h2>
          <p style={{ color: '#7A766F', fontSize: '0.9rem', marginBottom: '1.25rem' }}>
            {createDate && new Date(createDate + 'T00:00:00').toLocaleDateString('en-GB', {
              weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
            })}
            {createTime && ` at ${createTime}`}
          </p>

          <div className="form-group">
            <label>Date *</label>
            <input type="date" value={createDate} onChange={e => setCreateDate(e.target.value)} min={formatDate(new Date())} />
          </div>
          <div className="form-group">
            <label>Time *</label>
            <select value={createTime} onChange={e => setCreateTime(e.target.value)}>
              <option value="">Select time...</option>
              {generateTimeSlots().map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ display: 'flex', gap: '0.5rem' }}>
            <div style={{ flex: 1 }}>
              <label>First Name *</label>
              <input type="text" value={createFirstName} onChange={e => setCreateFirstName(e.target.value)} placeholder="First name" />
            </div>
            <div style={{ flex: 1 }}>
              <label>Last Name *</label>
              <input type="text" value={createLastName} onChange={e => setCreateLastName(e.target.value)} placeholder="Last name" />
            </div>
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input type="tel" value={createPhone} onChange={e => setCreatePhone(e.target.value)} placeholder="+852 9123 4567" />
          </div>
          <div className="form-group">
            <label>Email *</label>
            <input type="email" value={createEmail} onChange={e => setCreateEmail(e.target.value)} placeholder="email@example.com" />
          </div>
          <div className="form-group">
            <label>Source</label>
            <select value={createSource} onChange={e => setCreateSource(e.target.value)}>
              <option value="manual">Manual (Staff)</option>
              <option value="website">Website</option>
              <option value="meta">Meta Ads</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="phone">Phone Call</option>
              <option value="walk_in">Walk-in</option>
            </select>
          </div>
          <div className="form-group">
            <label>Notes</label>
            <input type="text" value={createNotes} onChange={e => setCreateNotes(e.target.value)} placeholder="Optional notes..." />
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexDirection: isMobile ? 'column' : 'row' }}>
            <button className="btn btn-primary" onClick={handleCreate} disabled={createSubmitting} style={{ flex: 1 }}>
              {createSubmitting ? 'Creating...' : 'Create Booking'}
            </button>
            <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>
              Cancel
            </button>
          </div>
        </BookingModal>
      )}

      {/* ======================== RESCHEDULE MODAL ======================== */}
      {showReschedule && (
        <BookingModal isMobile={isMobile} onClose={() => setShowReschedule(null)}>
          <h2 style={{ marginTop: 0, marginBottom: '0.25rem' }}>Reschedule</h2>
          <p style={{ color: '#7A766F', fontSize: '0.9rem', marginBottom: '1.25rem' }}>
            {showReschedule.customer_name}
          </p>

          <div className="form-group">
            <label>New Date</label>
            <input type="date" value={rescheduleDate} onChange={e => setRescheduleDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label>New Time</label>
            <select value={rescheduleTime} onChange={e => setRescheduleTime(e.target.value)}>
              <option value="">Select time...</option>
              {generateTimeSlots().map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexDirection: isMobile ? 'column' : 'row' }}>
            <button className="btn btn-primary" onClick={handleReschedule} disabled={rescheduleSubmitting} style={{ flex: 1 }}>
              {rescheduleSubmitting ? 'Rescheduling...' : 'Confirm'}
            </button>
            <button className="btn btn-secondary" onClick={() => setShowReschedule(null)}>
              Cancel
            </button>
          </div>
        </BookingModal>
      )}
    </div>
  );
}